import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, queryAssignedNodes } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { BlocksElement } from '../BlocksElement';
import { BlocksCheckboxField } from '../checkbox-field/checkbox-field';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import type { FieldsetOrientation, FieldsetRole, FieldsetStatus } from './fieldset.model';
import styles from './fieldset.styles';

/**
 * @slot default - The component content
 * @slot error - error slot
 * @slot field-note - default field-note
 * @slot warning - warningNote slot
 *
 * @csspart base - The component's base wrapper.
 * @csspart legend - The component's legend element.
 * @csspart body - The component's body field.
 * @csspart list- The component's list field.
 * @csspart part-required- The component's required element.
 * @csspart part-default - The component's default slot.
 * @csspart loader - The component's skeleton loader element.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart warning-note- The component's warning-note wrapper.
 * @csspart part-warning - The component's warning slot.
 * @csspart warning-icon - The component's warning icon.
 * @csspart error-note- The component's error-note wrapper.
 * @csspart part-error - The component's error slot.
 * @csspart error-icon - The component's error icon.
 */
export class BlocksFieldset extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Status of fieldSet
   */
  @property({ type: String })
  status: FieldsetStatus;

  /**
   * disabled attribute
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  /**
   *fieldnote for checkbox
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   * Errornote text
   */

  @property({ type: String })
  errorNote: string | undefined;

  /**
   * Warningnote text
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * Required attribute
   */
  @property({ type: Boolean })
  required: boolean | undefined;

  /**
   * Visibly hide the legend
   * 1) Removes the legend from visual users but can still be read by assistive technology
   */
  @property({ type: Boolean })
  hideLegend: boolean | undefined;

  /**
   * Notation is used for setting required or optional label
   */
  @property({ type: String })
  notation: string | undefined;

  /**
   * Inverted variant
   * 1) Used for dark backgrounds
   */
  @property({ type: Boolean })
  inverted: boolean | undefined;

  /**
   * aria-describedby attribute property
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * aria-labelledby attribute property
   */
  @property({ type: String })
  ariaLabelledBy: string | undefined;

  /**
   * The form field's label
   */
  @property({ type: String })
  label: string | undefined;

  /**
   * Aria label attribute
   * - Provides an accessible name for the fieldset element
   */
  @property({ type: String })
  arialabel: string | undefined;

  /**
   * Horizontal checkbox display
   */
  @property({ type: String })
  orientation: FieldsetOrientation;

  /**
   * Query all the checkbox-field
   */
  @queryAssignedNodes({ flatten: true })
  private checkboxFieldItems: Array<BlocksCheckboxField> | undefined;

  /**
   * Set the fieldset role.
   */
  @property({ type: String })
  fieldsetRole: FieldsetRole;

  firstUpdated() {
    if (this.querySelector('cat-checkbox-field')) {
      // Set the checkbox role to list
      this.fieldsetRole = 'list';
      this.checkboxFieldItems?.forEach((checkboxFieldItems) => {
        checkboxFieldItems.roleForCheckboxField = 'listitem';
      });
    }
    // Set the radio group role to radiogroup
    if (this.querySelector('cat-radio-field')) {
      this.fieldsetRole = 'radiogroup';
    }
  }

  private setAriaDescribedBy() {
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
    }
  }

  /**
   * Display error note from slot or from errorNote property
   */
  private getErrorNote() {
    if ((this.status === 'error' && this.errorNote) || this.slotNotEmpty('error')) {
      return html`
        <div part="error-note" class="cat-c-errornote">
          <cat-icon-warning-diamond-filled
            part="error-icon"
            exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
            color="error"
            class="cat-c-fieldset__error-icon"
          ></cat-icon-warning-diamond-filled>
          <cat-field-note
            part="field-note-wrapper"
            exportparts=${this.getExportParts('field-note')}
            ?isDisabled=${this.disabled}
            id=${ifDefined(this.ariaDescribedBy)}
            .status=${'error'}
          >
            <slot part="part-error" name="error">${this.errorNote}</slot>
          </cat-field-note>
        </div>
      `;
    }
  }

  /**
   * Display warning note from slot or from warningNote property
   */

  private getWarningNote() {
    if ((this.status === 'warning' && this.warningNote) || this.slotNotEmpty('warning')) {
      return html`
        <div part="warning-note" class="cat-c-errornote">
          <cat-icon-warning-triangle-filled
            part="warning-icon"
            exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
            color="knockout-brand"
            class="cat-c-fieldset__error-icon"
          ></cat-icon-warning-triangle-filled>
          <cat-field-note
            part="field-note-wrapper"
            exportparts=${this.getExportParts('field-note')}
            ?isDisabled=${this.disabled}
            id=${ifDefined(this.ariaDescribedBy)}
            .status=${'warning'}
          >
            <slot part="part-warning" name="warning">${this.warningNote}</slot>
          </cat-field-note>
        </div>
      `;
    }
  }

  /**
   * 1) Display field note from slot or from fieldNote property
   */
  private getFieldNote() {
    /* 1 */
    if ((this.status !== 'error' && this.status !== 'warning' && this.fieldNote) || this.slotNotEmpty('field-note')) {
      return html`
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          ?isDisabled=${this.disabled}
          id=${ifDefined(this.ariaDescribedBy)}
        >
          <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
        </cat-field-note>
      `;
    }
  }

  private hasFieldNote() {
    return (
      this.fieldNote ||
      this.slotNotEmpty('field-note') ||
      this.errorNote ||
      this.warningNote ||
      this.slotNotEmpty('error') ||
      this.slotNotEmpty('warning')
    );
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-fieldset', {
      'cat-c-fieldset--inverted': this.inverted === true,
      'cat-c-fieldset--required': this.required,
      'cat-c-fieldset--disabled': this.disabled === true,
      'cat-c-fieldset--horizontal': this.orientation === 'horizontal'
    });
    this.setAriaDescribedBy();
    return html` <fieldset
      part="base"
      class="${componentClassNames}"
      aria-describedby="${ifDefined(this.ariaDescribedBy)}"
      aria-labelledby="${ifDefined(this.ariaLabelledBy)}"
    >
      <legend
        part="legend"
        id="${ifDefined(this.ariaLabelledBy)}"
        class=${this.hideLegend || !this.label ? 'cat-c-fieldset__legend cat-u-is-vishidden' : 'cat-c-fieldset__legend'}
      >
        ${this.label} ${this.required ? html`<span part="part-required" class="cat-c-fieldset__required">${this.notation}</span>` : nothing}
      </legend>

      <div part="body" class="cat-c-fieldset__body">
        <div part="list" role="${ifDefined(this.fieldsetRole)}" aria-label=${ifDefined(this.arialabel)} class="cat-c-fieldset__list">
          <slot part="part-default"></slot>
        </div>
      </div>
      <!--- field note -->
      ${this.getFieldNote()}
      <!--- error note -->
      ${this.getErrorNote()}
      <!--- warning note -->
      ${this.getWarningNote()}
    </fieldset>`;
  }
}

if (customElements.get('cat-fieldset') === undefined) {
  customElements.define('cat-fieldset', BlocksFieldset);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-fieldset': BlocksFieldset;
  }
}
